/* eslint-disable @typescript-eslint/no-explicit-any */
'use client';

import { useEffect } from 'react';
import { usePathname } from 'next/navigation';
import { isClientProductionEnv } from '@/lib/observability';

declare global {
  interface Window {
    ['sourcepoint/utility']: {
      isVendorAllowed: (vendorName: string) => Promise<{
        allowed: boolean;
        gdprApplies: boolean;
        gdprAllowed: boolean;
        mspsApplies: boolean;
        mspsAllowed: boolean;
        ccpaApplies: boolean;
        ccpaAllowed: boolean;
      }>;
      isTcfAllowed: () => Promise<{
        allowed: boolean;
        gdprApplies: boolean;
        gdprAllowed: boolean;
      }>;
      isGppAllowed: () => Promise<{
        allowed: boolean;
        mspsApplies: boolean;
        mspsAllowed: boolean;
      }>;
    };
    __tcfapi?: (
      command: string,
      version: number,
      callback: (tcData: any, success: boolean) => void,
      parameter?: string | number
    ) => void;
    fbq?: (arg1: string, arg2: string, ...rest: any[]) => void;
    _fbq?: any;
    thirdPartyDataLayer?: any[];
    gtag?: (...args: any[]) => void;
    dataLayer?: any[];
    _hsq?: any[];
    _linkedin_partner_id?: string;
    _linkedin_data_partner_ids?: string[];
    lintrk?: (a: string, b: any) => void | { q: Array<[string, any]> };
    ttq?: any;
    _cio?: Array<unknown> & {
      identify?: (traits: Record<string, unknown>) => void;
    };
    _consentManagerLoaded?: Set<string>;
  }
}

type VendorScripts = {
  [key: string]: {
    scriptId: string;
    src?: string;
    inline?: string;
    onLoad?: () => void;
    pages?: string[];
  };
};

const VENDOR_SCRIPTS: VendorScripts = {
  hotjar: {
    scriptId: 'hotjar-script',
    inline: `
    (function(h,o,t,j,a,r){
      // Check if Hotjar is already loaded
      if (h.hj && h._hjSettings) return;
      
      h.hj=h.hj||function(){(h.hj.q=h.hj.q||[]).push(arguments)};
      h._hjSettings={hjid:870031,hjsv:6};
      a=o.getElementsByTagName('head')[0];
      r=o.createElement('script');r.async=1;
      r.src=t+h._hjSettings.hjid+j+h._hjSettings.hjsv;
      a.appendChild(r);
    })(window,document,'https://static.hotjar.com/c/hotjar-','.js?sv=');
    `,
  },
  bing: {
    scriptId: 'bing-script',
    inline: `
    (function (w, d, t, r, u) {
      // Check if Bing UET is already loaded
      if (w[u] && w[u].push) return;
      
      var f, n, i;
      (w[u] = w[u] || []),
        (f = function () {
          var o = { ti: "148006416" };
          (o.q = w[u]), (w[u] = new UET(o)), w[u].push("pageLoad");
        }),
        (n = d.createElement(t)),
        (n.src = r),
        (n.async = 1),
        (n.onload = n.onreadystatechange =
          function () {
            var s = this.readyState;
            (s && s !== "loaded" && s !== "complete") ||
              (f(), (n.onload = n.onreadystatechange = null));
          }),
        (i = d.getElementsByTagName(t)[0]),
        i.parentNode.insertBefore(n, i);
    })(window, document, "script", "//bat.bing.com/bat.js", "uetq");
    // enable TCF
    window.uetq = window.uetq || [];
    window.uetq.push("config", "tcf", { "enabled" : true });
    `,
  },
  capterra: {
    scriptId: 'capterra-script',
    inline: `
    (function() {
      if (!document.getElementById('capterra-tracking-img')) {
        const capterraImg = document.createElement("img");
        capterraImg.id = 'capterra-tracking-img';
        capterraImg.src = "https://ct.capterra.com/capterra_tracker.gif?vid=2095332&vkey=eea96217aab93b4cd8f88d664ac3c6e5";
        capterraImg.style.display = 'none';
        const span = document.createElement("span");
        span.appendChild(capterraImg);
        document.body.appendChild(span);
      }
    })();
    `,
  },
  convert: {
    scriptId: 'convert-script',
    inline: `
    (function (d) {
      // Check if Convert script is already loaded
      if (d.querySelector('script[src*="convertexperiments.com"]')) return;
      
      var s = d.createElement("script");
      s.type = "text/javascript";
      s.src = "//cdn-4.convertexperiments.com/v1/js/10042341-10042997.js";
      d.getElementsByTagName("head")[0].appendChild(s);
    })(document);
    `,
  },
  facebook: {
    scriptId: 'facebook-pixel-script',
    inline: `
    if (!window.fbq) {
      !function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?
      n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;
      n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;
      t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,
      document,'script','https://connect.facebook.net/en_US/fbevents.js');
      fbq('init', '1696408607309042');
      fbq('track', 'PageView');
    }

    (function() {
      if (!document.getElementById('fb-pixel-img')) {
        const fbImg = document.createElement('img');
        fbImg.id = 'fb-pixel-img';
        fbImg.height = 1;
        fbImg.width = 1;
        fbImg.style.display = 'none';
        fbImg.src = 'https://www.facebook.com/tr?id=1696408607309042&ev=PageView&noscript=1';
        document.body.appendChild(fbImg);
      }
    })();
    `,
  },
  googleAds: {
    scriptId: 'google-ads-script',
    src: 'https://www.googletagmanager.com/gtag/js?id=AW-877246914&l=thirdPartyDataLayer',
    inline: `
    if (!window.thirdPartyDataLayer) {
      window.thirdPartyDataLayer = window.thirdPartyDataLayer || [];
      function gtag(){thirdPartyDataLayer.push(arguments);}
      gtag('js', new Date());
      gtag('config', 'AW-877246914');
    }
    `,
  },
  growsurf: {
    scriptId: 'growsurf-script',
    pages: ['/'],
    inline: `
    if (!document.getElementById("growsurf-script-inline")) {
      (function(g,r,s,f){
        g.grsfSettings={campaignId:"1os1er",version:"2.0.0"};
        s=r.getElementsByTagName("head")[0];
        f=r.createElement("script");
        f.async=1;
        f.id="growsurf-script-inline";
        f.src="https://app.growsurf.com/growsurf.js?v="+g.grsfSettings.version;
        f.setAttribute("grsf-campaign", g.grsfSettings.campaignId);
        s.appendChild(f);
      })(window,document);
    }
    `,
  },
  hubspot: {
    scriptId: 'hubspot-script',
    src: '//js.hs-scripts.com/7088586.js?businessUnitId=33104',
    inline: `
    if (!window._hsq) {
      var _hsq = window._hsq = window._hsq || [];
    }
    `,
  },
  hyros: {
    scriptId: 'hyros-tracking-script',
    inline: `
      if (!document.querySelector('script[src*="hyros.com"]')) {
        (function() {
          var head = document.head;
          var script = document.createElement('script');
          script.type = 'text/javascript';
          script.src = "https://t.signupgenius.com/v1/lst/universal-script?ph=741472999a3031cebcb158a88f18920731bd0a357eb1952d01b0abec228909f0&tag=!clicked&spa=true&ref_url=" + encodeURI(document.URL);
          head.appendChild(script);
        })();
      }
    `,
  },
  linkedIn: {
    scriptId: 'linkedin-script',
    inline: `
    if (!window._linkedin_data_partner_ids) {
      _linkedin_partner_id = "3708418";
      window._linkedin_data_partner_ids = window._linkedin_data_partner_ids || [];
      window._linkedin_data_partner_ids.push(_linkedin_partner_id);
      
      (function(l) {
        if (!l){window.lintrk = function(a,b){window.lintrk.q.push([a,b])};
        window.lintrk.q=[]}
        var s = document.getElementsByTagName("script")[0];
        var b = document.createElement("script");
        b.type = "text/javascript";b.async = true;
        b.src = "https://snap.licdn.com/li.lms-analytics/insight.min.js";
        s.parentNode.insertBefore(b, s);
      })(window.lintrk);
    }

    (function() {
      if (!document.getElementById('linkedin-tracking-img')) {
        const linkedInImg = document.createElement("img");
        linkedInImg.id = 'linkedin-tracking-img';
        linkedInImg.height = 1;
        linkedInImg.width = 1;
        linkedInImg.style.display = "none";
        linkedInImg.alt = "";
        linkedInImg.src = "https://px.ads.linkedin.com/collect/?pid=3708418&fmt=gif";
        document.body.appendChild(linkedInImg);
      }
    })();
    `,
  },
  optInMonster: {
    scriptId: 'optin-monster-script',
    inline: `
    if (!document.querySelector('#optin-monster-api-script') && !document.querySelector('script[src*="omappapi.com"]')) {
      (function (d, u, ac) {
        var s = d.createElement("script");
        s.id = "optin-monster-api-script";
        s.type = "text/javascript";
        s.src = "https://a.omappapi.com/app/js/api.min.js";
        s.async = true;
        s.dataset.user = u;
        s.dataset.account = ac;
        d.getElementsByTagName("head")[0].appendChild(s);
      })(document, 126907, 139257);
  }
    `,
  },
  tiktok: {
    scriptId: 'tiktok-pixel-script',
    inline: `
    if (!window.ttq) {
      !function (w, d, t) {   w.TiktokAnalyticsObject=t;var ttq=w[t]=w[t]||[];ttq.methods=["page","track","identify","instances","debug","on","off","once","ready","alias","group","enableCookie","disableCookie","holdConsent","revokeConsent","grantConsent"],ttq.setAndDefer=function(t,e){t[e]=function(){t.push([e].concat(Array.prototype.slice.call(arguments,0)))}};for(var i=0;i<ttq.methods.length;i++)ttq.setAndDefer(ttq,ttq.methods[i]);ttq.instance=function(t){for( var e=ttq._i[t]||[],n=0;n<ttq.methods.length;n++)ttq.setAndDefer(e,ttq.methods[n]);return e},ttq.load=function(e,n){var r="https://analytics.tiktok.com/i18n/pixel/events.js",o=n&&n.partner;ttq._i=ttq._i||{},ttq._i[e]=[],ttq._t=ttq._t||{},ttq._t[e]=+new Date,ttq._o=ttq._o||{},ttq._o[e]=n||{};n=document.createElement("script");n.type="text/javascript",n.async=!0,n.src=r+"?sdkid="+e+"&lib="+t;e=document.getElementsByTagName("script")[0];e.parentNode.insertBefore(n,e)};   ttq.load('D72OGHRC77UDBCCMF24G');   ttq.page(); }(window, document, 'ttq');
    }
    `,
  },
  blitzRocket: {
    scriptId: 'blitzrocket-script',
    inline: `
    if (!document.querySelector('script[src*="blitzrocket.js"]')) {
      (function() {
        const blitzRocketScript = document.createElement('script');
        blitzRocketScript.id = 'blitzrocket-loader-script';
        blitzRocketScript.src = 'https://blitzrocket.com/blitzrocket.js';
        document.body.appendChild(blitzRocketScript);
      })();
    }
    `,
  },
  customerIO: {
    scriptId: 'customerio-script',
    inline: `
      if (!document.getElementById('cio-tracker')) {
        var _cio = window._cio = window._cio || [];
        (function() {
          var a,b,c;a=function(f){return function(){_cio.push([f].concat(Array.prototype.slice.call(arguments,0)))}};
          b=["load","identify","sidentify","track","page","on","off"];
          for(c=0;c<b.length;c++){_cio[b[c]]=a(b[c])};
          var t = document.createElement('script'),
              s = document.getElementsByTagName('script')[0];
          t.async = true;
          t.id = 'cio-tracker';
          t.setAttribute('data-site-id', '64b9931f6cb7b471c0aa');
          t.setAttribute('data-use-array-params', 'true');
          t.setAttribute('data-use-in-app', 'true');
          t.src = 'https://assets.customer.io/assets/track.js';
          s.parentNode.insertBefore(t, s);
        })();
      }
    `,
  },
  ...(isClientProductionEnv()
    ? {
        newRelic: {
          scriptId: 'new-relic-script',
          src: '/newrelic-browser-agent.snippet.js',
        },
      }
    : {}),
  // Add other vendor scripts here
};

export default function ConsentManager() {
  const pathname = usePathname();

  useEffect(() => {
    // Initialize global script tracking
    if (!window._consentManagerLoaded) {
      window._consentManagerLoaded = new Set<string>();
    }

    // Prevent multiple instances from running simultaneously
    if (window._consentManagerLoaded.has('_consent_manager_instance')) {
      return;
    }
    window._consentManagerLoaded.add('_consent_manager_instance');

    // Function to handle vendor scripts based on consent
    const handleVendorScripts = async () => {
      for (const vendorName of Object.keys(VENDOR_SCRIPTS)) {
        try {
          const scriptConfig = VENDOR_SCRIPTS[vendorName];

          const vendorConsent =
            await window['sourcepoint/utility'].isVendorAllowed(vendorName);
          if (vendorConsent.allowed) {
            const notLoaded = !window._consentManagerLoaded!.has(
              scriptConfig.scriptId
            );
            const pageMatch =
              !scriptConfig.pages || scriptConfig.pages.includes(pathname);

            if (notLoaded && pageMatch) {
              addScript(scriptConfig);
              window._consentManagerLoaded!.add(scriptConfig.scriptId);
            }
          } else {
            removeScript(scriptConfig.scriptId);
            window._consentManagerLoaded!.delete(scriptConfig.scriptId);
          }
        } catch {}
      }
    };

    const handleConsentChange = () => {
      if (typeof window.__tcfapi === 'function') {
        // Handle initial consent state
        window.__tcfapi('getTCData', 2, async (tcData, success) => {
          if (success) {
            await handleVendorScripts();
          }
        });

        // Listen for consent changes
        window.__tcfapi('addEventListener', 2, async (tcData, success) => {
          if (success) {
            if (
              tcData.eventStatus === 'useractioncomplete' ||
              tcData.eventStatus === 'tcloaded'
            ) {
              await handleVendorScripts();
            }
          }
        });
      }
    };

    const addScript = (scriptConfig: VendorScripts[keyof VendorScripts]) => {
      // Check if script already exists in the DOM
      const existingScript = document.getElementById(scriptConfig.scriptId);
      const existingInlineScript = document.getElementById(
        `${scriptConfig.scriptId}-inline`
      );

      if (existingScript || existingInlineScript) {
        return;
      }

      // If external script exists, add it first
      if (scriptConfig.src) {
        const externalScript = document.createElement('script');
        externalScript.id = scriptConfig.scriptId;
        externalScript.src = scriptConfig.src;
        externalScript.async = true;

        // Only add inline script after external script loads
        externalScript.onload = () => {
          // Add inline script if it exists and doesn't already exist
          if (
            scriptConfig.inline &&
            !document.getElementById(`${scriptConfig.scriptId}-inline`)
          ) {
            const inlineScript = document.createElement('script');
            inlineScript.id = `${scriptConfig.scriptId}-inline`;
            inlineScript.textContent = scriptConfig.inline;

            inlineScript.onload = () => {
              if (scriptConfig.onLoad) {
                scriptConfig.onLoad();
              }
            };

            document.head.appendChild(inlineScript);
          }
        };

        document.head.appendChild(externalScript);
      } else if (scriptConfig.inline) {
        // If only inline script exists
        const script = document.createElement('script');
        script.id = scriptConfig.scriptId;
        script.textContent = scriptConfig.inline;

        script.onload = () => {
          if (scriptConfig.onLoad) {
            scriptConfig.onLoad();
          }
        };

        document.head.appendChild(script);
      }
    };

    const removeScript = (scriptId: string) => {
      // Remove main script
      const existingScript = document.getElementById(scriptId);
      if (existingScript) {
        existingScript.remove();
      }

      // Remove inline script
      const existingInlineScript = document.getElementById(
        `${scriptId}-inline`
      );
      if (existingInlineScript) {
        existingInlineScript.remove();
      }
    };

    // Wait for sourcePointUtils to be available
    const waitForDependencies = () => {
      const initInterval = setInterval(() => {
        if (
          window['sourcepoint/utility'] &&
          typeof window.__tcfapi === 'function'
        ) {
          clearInterval(initInterval);
          handleConsentChange();
        }
      }, 500);

      return initInterval;
    };

    const initInterval = waitForDependencies();

    return () => {
      clearInterval(initInterval);

      // Remove instance tracking
      window._consentManagerLoaded?.delete('_consent_manager_instance');

      // Clean up all scripts when component unmounts
      Object.values(VENDOR_SCRIPTS).forEach((scriptConfig) => {
        removeScript(scriptConfig.scriptId);
        window._consentManagerLoaded?.delete(scriptConfig.scriptId);
      });

      if (typeof window.__tcfapi === 'function') {
        window.__tcfapi('removeEventListener', 2, () => {}, 1);
      }
    };
  }, [pathname]);

  return null;
}
